import { useEffect, useRef } from 'react';
import { playSoundEffect } from '../hooks/useSoundEffects';

/**
 * Hook to track newly deployed drones and play deployment sound
 * Compares previous and current game state to detect new drone deployments
 */
export const useDroneDeploymentAnimations = (gameState, userId) => {
  const previousDronesRef = useRef(new Map());
  const isFirstRenderRef = useRef(true);

  useEffect(() => {
    if (!gameState?.state || !userId) return;
    
    // ✅ Skip detection on first render - we don't want to play sounds for drones already deployed
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false;
      // Still build the map for next comparison
      const currentDrones = new Map();
      const allPlayerIds = gameState.players || Object.keys(gameState.state);
      allPlayerIds.forEach(playerId => {
        const player = gameState.state[playerId];
        if (player?.drones) {
          player.drones.forEach((drone) => {
            if (drone && drone.instanceId && !drone.markedForRemoval) {
              // Use instanceId as key to track drones regardless of array position
              const key = `${playerId}_${drone.instanceId}`;
              currentDrones.set(key, drone.instanceId);
            }
          });
        }
      });
      previousDronesRef.current = currentDrones;
      return; // Don't process deployments on first render
    }

    const currentDrones = new Map();
    const allPlayerIds = gameState.players || Object.keys(gameState.state);

    // Build map of current drones by instanceId (not by index, since indices can change)
    allPlayerIds.forEach(playerId => {
      const player = gameState.state[playerId];
      if (!player?.drones) return;

      player.drones.forEach((drone) => {
        if (drone && drone.instanceId && !drone.markedForRemoval) {
          // Use instanceId as key to track drones regardless of array position
          const key = `${playerId}_${drone.instanceId}`;
          currentDrones.set(key, drone.instanceId);
        }
      });
    });

    // Detect newly deployed drones
    const newDeployments = [];
    currentDrones.forEach((instanceId, key) => {
      const previousInstanceId = previousDronesRef.current.get(key);
      
      // ✅ Only detect as newly deployed if this drone wasn't in the previous state
      const wasEmpty = previousInstanceId === undefined || previousInstanceId === null;
      
      if (wasEmpty && instanceId) {
        // This drone is new - it's a deployment
        newDeployments.push({ key, instanceId });
      }
    });

    // ✅ Play drone deploy sound for each newly deployed drone
    if (newDeployments.length > 0) {
      newDeployments.forEach((deployment, idx) => {
        const { key, instanceId } = deployment;
        const [playerId] = key.split('_');
        const player = gameState.state[playerId];
        
        // Find drone by instanceId (not by index, since indices can change)
        const drone = player?.drones?.find(d => d && d.instanceId === instanceId && !d.markedForRemoval);
        
        if (drone) {
          // Small delay to stagger sounds if multiple drones deploy at once
          const delay = newDeployments.length > 1 ? idx * 100 : 0;
          setTimeout(() => {
            try {
              playSoundEffect('/sounds/drone.mp3', 0.5); // Play at 50% volume
            } catch (error) {
              console.error(`❌ Error playing drone deploy sound:`, error);
            }
          }, delay);
        } else {
          console.warn(`⚠️ DRONE DEPLOY SOUND: Drone with instanceId ${instanceId} not found for player ${playerId}`);
        }
      });
    }

    // Update previous drones reference
    previousDronesRef.current = currentDrones;
  }, [gameState, userId]);
};

